import type { AccessState, ToolAvailability, ToolCardModel, ToolId } from '../contracts/types'

const TOOL_META: Record<
  ToolId,
  Omit<ToolCardModel, 'toolId' | 'availability'>
> = {
  guided_studio: {
    label: 'Guided Studio',
    purpose: 'Capture signal, frame intent, and shape early structure.',
    recommendedUse: 'When grounding mood, scenes, and creative direction.',
    ctaLabel: 'Explore Tool',
  },
  omnistudio: {
    label: 'OmniStudio',
    purpose: 'Structure compile and authorship handoff.',
    recommendedUse: 'After structure is coherent and handoff is approved.',
    ctaLabel: 'Review Preview',
  },
  omnicine: {
    label: 'OmniCine',
    purpose: 'Cinematic timeline execution readiness.',
    recommendedUse: 'When timeline logic is ready.',
    ctaLabel: 'Coming Soon',
  },
  omnicast: {
    label: 'OmniCast',
    purpose: 'Controlled presentation, witness, and showcase readiness.',
    recommendedUse: 'Pitch rooms, table reads, investor previews — not public release.',
    ctaLabel: 'Coming Soon',
  },
  scapes_worlds: {
    label: 'Scapes / Worlds',
    purpose: 'Persistent environment and world-building layer.',
    recommendedUse: 'Before XR — cinematic worlds, virtual sets, NPC habitats.',
    ctaLabel: 'Coming Soon',
  },
  xr_worlds: {
    label: 'XR Worlds',
    purpose: 'Immersive spatial runtime extension.',
    recommendedUse: 'After Scape / World layer is ready.',
    ctaLabel: 'Coming Soon',
  },
  rights_passport: {
    label: 'Rights Passport',
    purpose: 'Ownership, consent, and provenance review.',
    recommendedUse: 'Before external presentation or distribution paths.',
    ctaLabel: 'Review',
  },
  request_access: {
    label: 'Request Access',
    purpose: 'Controlled access to SynkTribe creator pathways.',
    recommendedUse: 'When your account is not yet granted build access.',
    ctaLabel: 'Request Access',
  },
  book_demo: {
    label: 'Book Demo',
    purpose: 'Guided walkthrough with honest product boundaries.',
    recommendedUse: 'For studio, investor, or creator onboarding conversations.',
    ctaLabel: 'Book Demo',
  },
}

type MatrixRow = Partial<Record<ToolId, ToolAvailability>>

const MATRIX: Record<AccessState, MatrixRow> = {
  visitor: {
    guided_studio: 'request_access',
    omnistudio: 'locked',
    omnicine: 'locked',
    omnicast: 'locked',
    scapes_worlds: 'locked',
    xr_worlds: 'locked',
    rights_passport: 'locked',
    request_access: 'available',
    book_demo: 'available',
  },
  requested_access: {
    guided_studio: 'preview',
    omnistudio: 'locked',
    omnicine: 'locked',
    omnicast: 'locked',
    scapes_worlds: 'locked',
    xr_worlds: 'locked',
    rights_passport: 'preview',
    request_access: 'available',
    book_demo: 'available',
  },
  demo_booked: {
    guided_studio: 'preview',
    omnistudio: 'preview',
    omnicine: 'coming_soon',
    omnicast: 'coming_soon',
    scapes_worlds: 'coming_soon',
    xr_worlds: 'coming_soon',
    rights_passport: 'preview',
    request_access: 'available',
    book_demo: 'available',
  },
  creator_beta: {
    guided_studio: 'available',
    omnistudio: 'preview',
    omnicine: 'coming_soon',
    omnicast: 'coming_soon',
    scapes_worlds: 'coming_soon',
    xr_worlds: 'locked',
    rights_passport: 'preview',
    request_access: 'available',
    book_demo: 'available',
  },
  studio_access: {
    guided_studio: 'available',
    omnistudio: 'beta',
    omnicine: 'preview',
    omnicast: 'preview',
    scapes_worlds: 'preview',
    xr_worlds: 'preview',
    rights_passport: 'available',
    request_access: 'available',
    book_demo: 'available',
  },
  founder_mode: {
    guided_studio: 'available',
    omnistudio: 'available',
    omnicine: 'preview',
    omnicast: 'preview',
    scapes_worlds: 'preview',
    xr_worlds: 'preview',
    rights_passport: 'available',
    request_access: 'available',
    book_demo: 'available',
  },
  admin_mode: {
    guided_studio: 'available',
    omnistudio: 'available',
    omnicine: 'available',
    omnicast: 'available',
    scapes_worlds: 'available',
    xr_worlds: 'available',
    rights_passport: 'available',
    request_access: 'available',
    book_demo: 'available',
  },
  enterprise_review: {
    guided_studio: 'preview',
    omnistudio: 'preview',
    omnicine: 'coming_soon',
    omnicast: 'preview',
    scapes_worlds: 'coming_soon',
    xr_worlds: 'locked',
    rights_passport: 'preview',
    request_access: 'available',
    book_demo: 'available',
  },
  investor_preview: {
    guided_studio: 'preview',
    omnistudio: 'preview',
    omnicine: 'coming_soon',
    omnicast: 'preview',
    scapes_worlds: 'coming_soon',
    xr_worlds: 'locked',
    rights_passport: 'preview',
    request_access: 'available',
    book_demo: 'available',
  },
}

const TOOL_ORDER: ToolId[] = [
  'guided_studio',
  'omnistudio',
  'omnicine',
  'omnicast',
  'scapes_worlds',
  'xr_worlds',
  'rights_passport',
  'request_access',
  'book_demo',
]

export function buildToolCards(accessState: AccessState): ToolCardModel[] {
  const row = MATRIX[accessState] ?? MATRIX.visitor
  return TOOL_ORDER.map((toolId) => {
    const availability = row[toolId] ?? 'locked'
    const meta = TOOL_META[toolId]
    return {
      toolId,
      availability,
      ...meta,
      ctaLabel: ctaLabelForAvailability(availability, meta.ctaLabel),
    }
  })
}

function ctaLabelForAvailability(
  availability: ToolAvailability,
  defaultLabel: string
): string {
  switch (availability) {
    case 'coming_soon':
      return 'Coming Soon'
    case 'locked':
    case 'unavailable':
      return 'Locked'
    case 'request_access':
      return 'Request Access'
    case 'preview':
      return 'Review Preview'
    case 'beta':
      return 'Explore Beta'
    default:
      return defaultLabel
  }
}

export function isToolStartAllowed(availability: ToolAvailability): boolean {
  return availability === 'available' || availability === 'beta'
}

export function getToolAvailability(
  accessState: AccessState,
  toolId: ToolId
): ToolAvailability {
  const row = MATRIX[accessState] ?? MATRIX.visitor
  return row[toolId] ?? 'locked'
}
